import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useLocale, useTranslations } from 'gt-react'
import { History } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { EntityList, type EntityListColumn } from '@/components/crm/entity-list'
import { useServerList } from '@/components/data-table/use-server-list'
import { PageHeader } from '@/components/layout/page-header'
import { useEntityListNav } from '@/components/layout/use-entity-nav'
import { Badge } from '@/components/ui/badge'
import { formatDateTime } from '@/lib/intl-format'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/custom-modules/$key/audit')({
  component: AuditLogPage,
})

interface AuditRow {
  id: string
  at: Date
  actor: string
  action: string
  allowed: boolean
  details: string
}

function AuditLogPage() {
  const { key: moduleKey } = Route.useParams()
  const t = useTranslations()
  const locale = useLocale()

  // Zoeken, sorteren en pagineren gebeuren in de database; de pagineerbalk komt uit
  // `server={list.server}` (hij portalt naar de voettekst). De eigen
  // vorige/volgende-balk die hier stond is daarmee vervallen — twee balken naast
  // elkaar zouden elk hun eigen pagina bijhouden.
  const filters = useMemo(() => ({ moduleKey }), [moduleKey])
  const list = useServerList({
    entityKey: 'customAuditEntry',
    initialSorting: [{ id: 'at', desc: true }],
    filters,
  })
  const auditQ = useQuery(
    orpc.modules.custom.audit.list.queryOptions({ input: { ...list.input, ...filters } }),
  )
  const rows = useMemo<AuditRow[]>(
    () =>
      (auditQ.data?.rows ?? []).map((entry) => ({
        id: entry.id,
        at: entry.createdAt instanceof Date ? entry.createdAt : new Date(entry.createdAt),
        actor: entry.userId ? entry.userId.slice(0, 8) : '—',
        action: entry.action,
        allowed: entry.allowed,
        details: entry.metadata ? JSON.stringify(entry.metadata) : '—',
      })),
    [auditQ.data],
  )
  const total = auditQ.data?.total
  useEffect(() => {
    list.setTotal(total)
  }, [total, list.setTotal])

  // Read-only lijst: geen `toNew`, dus de kopbalk toont geen "+".
  useEntityListNav({
    entityKey: 'customAuditEntry',
    total: total ?? 0,
    search: list.search,
    onSearch: list.setSearch,
    sort: list.sort,
  })

  // Elke kolom met een sorteerpijl (`sortAccessor`) staat ook in `sortColumns` van
  // custom-audit.router.ts. De detailkolom rendert vrije JSON en is daarom bewust
  // niet sorteerbaar — precies zoals hiervoor.
  const columns = useMemo<EntityListColumn<AuditRow>[]>(
    () => [
      {
        key: 'at',
        header: t('builderRuntime.audit.col.at'),
        render: (r) => (
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {formatDateTime(r.at, locale)}
          </span>
        ),
        sortAccessor: (r) => r.at,
      },
      {
        key: 'actor',
        header: t('builderRuntime.audit.col.actor'),
        render: (r) => <span className="font-mono text-xs">{r.actor}</span>,
        sortAccessor: (r) => r.actor,
      },
      {
        key: 'action',
        header: t('builderRuntime.audit.col.action'),
        render: (r) => (
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs">{r.action}</span>
            <Badge variant={r.allowed ? 'default' : 'destructive'}>
              {r.allowed ? t('builderRuntime.audit.allowed') : t('builderRuntime.audit.denied')}
            </Badge>
          </div>
        ),
        sortAccessor: (r) => r.action,
      },
      {
        key: 'details',
        header: t('builderRuntime.audit.col.details'),
        render: (r) => (
          <span className="text-xs text-muted-foreground max-w-md truncate block">{r.details}</span>
        ),
      },
    ],
    [t, locale],
  )

  return (
    <>
      <PageHeader
        title={t('builderRuntime.audit.title')}
        subtitle={t('builderRuntime.audit.subtitle')}
      />
      <EntityList<AuditRow>
        rows={rows}
        columns={columns}
        isLoading={auditQ.isLoading}
        emptyState={
          <div className="flex flex-col items-center gap-2">
            <History className="h-8 w-8 text-muted-foreground opacity-40" />
            <h3 className="font-medium">{t('builderRuntime.audit.empty.title')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('builderRuntime.audit.empty.subtitle')}
            </p>
          </div>
        }
        server={list.server}
      />
    </>
  )
}
